import type { GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Label } from '@neo/primitives/label'
import InlineEdit from '@neo/shared/components/inline-edit'
import type { DeepKeys } from '@tanstack/react-form'
import { PlusIcon, X } from 'lucide-react'
import { v4 } from 'uuid'
import { TemplateSelect } from 'v2/pages/conversations/outbound/components/template-select'
import {
  VariableEditor,
  type WhatsAppFormType,
} from 'v2/pages/conversations/outbound/components/variable-editor'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import z from 'zod'
import { MediaUpload } from '../../components/media-upload/media-upload'
import { NodevarList } from '../../components/nodevar-list'
import { v2validations } from '../../config/validation.config'
import type { AgentPresetConversationNodeformData } from '../../types'

export const whatsappSpecificFields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericField<AgentPresetConversationNodeformData>[] => {
  const validationSchema = v2validations({ nodeVars }).agent_preset_conversation
  const baseSchema = validationSchema.innerType()

  return [
    {
      name: 'from',
      label: 'Send WhatsApp from',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp') return z.string().min(1, 'From is required')
        return z.string().optional()
      },
      componentType: 'input',
      visible: (values) => values.channel === 'whatsapp',
      onChangeListenTo: ['channel'],
      inputProps: {
        placeholder: 'Enter',
        endIcon: (
          <NodevarList
            nodevars={fieldsWithDynamicNodeVars.whatsapp_from || []}
            className="pr-2"
            inputElementSelector="[name=from]"
            replaceEntireField
          />
        ),
      },
      afterOnChange: (_, __, form) => {
        // Reset template and variables when from changes
        form?.setFieldValue('template', {})
        form?.setFieldValue('variables', {
          header: [],
          body: [],
        })
      },
    },
    {
      name: 'to',
      label: 'Send WhatsApp to',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp') {
          return z.string().min(1, 'To is required')
        }
        return z.string().optional()
      },
      componentType: 'input',
      visible: (values) => values.channel === 'whatsapp',
      onChangeListenTo: ['channel'],
      inputProps: {
        placeholder: 'Enter',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=to]" />
        ),
      },
    },
    {
      name: 'whatsapp_msg_type',
      label: 'WhatsApp message type',
      zodValidation: baseSchema.shape.whatsapp_msg_type,
      componentType: 'select',
      visible: (values) => values.channel === 'whatsapp',
      onChangeListenTo: ['channel'],
      afterOnChange: (_, __, form) => {
        // Reset fields based on message type
        form?.setFieldValue('template', {})
        form?.setFieldValue('variables', { header: [], body: [] })
        form?.setFieldValue('whatsapp_media', [])
        form?.setFieldValue('text', '')
        form?.setFieldValue('header_type', 'none')
        form?.setFieldValue('header_text', '')
        form?.setFieldValue('header_media', undefined)
        form?.setFieldValue('body', '')
        form?.setFieldValue('footer', '')

        const newMsgType = form?.state.values.whatsapp_msg_type
        if (newMsgType === 'whatsapp_button') {
          form?.setFieldValue('buttons', [{ title: 'Button 1', id: v4() }])
          form?.setFieldValue('wait_for_response', true)
          form?.setFieldValue('reply_timeout', 3600)
        } else {
          form?.setFieldValue('buttons', [])
        }
        if (newMsgType === 'whatsapp_list') {
          form?.setFieldValue('whatsapp_list', { button: 'Options' })
          form?.setFieldValue('sections', [
            {
              title: 'Section title 1',
              rows: [{ title: 'Row title 1', description: '', id: v4() }],
            },
          ])
          form?.setFieldValue('wait_for_response', true)
          form?.setFieldValue('reply_timeout', 3600)
        } else {
          form?.setFieldValue('sections', [])
        }
      },
      inputProps: {
        options: [
          { label: 'Text', value: 'text' },
          { label: 'Image/Audio/Video/Document', value: 'whatsapp_media' },
          { label: 'WhatsApp template', value: 'template' },
          { label: 'Message with buttons', value: 'whatsapp_button' },
          { label: 'Message with list', value: 'whatsapp_list' },
        ],
      },
    },
    // WhatsApp media upload field
    {
      name: 'whatsapp_media',
      type: 'custom',
      label: 'Upload media',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_media') {
          return baseSchema.shape.whatsapp_media.refine((files) => files && files.length > 0, {
            message: 'At least one media file must be attached',
          })
        }
        return baseSchema.shape.whatsapp_media
      },
      onChangeListenTo: ['whatsapp_msg_type', 'text', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_media',
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<AgentPresetConversationNodeformData>
        onChange: (value: unknown[]) => void
      }) => {
        const onURLChange = (uploadedFile: RFileUploadResponse) => {
          const mediaFiles = form.state.values.whatsapp_media || []
          const newMediaFile = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange([...mediaFiles, newMediaFile])
        }

        const onRemove = (fileToRemove: RFileUploadResponse) => {
          const mediaFiles = form.state.values.whatsapp_media || []
          const updatedMediaFiles = mediaFiles.filter(
            (file: { file_id?: string }) => file.file_id !== fileToRemove.file_id,
          )
          onChange(updatedMediaFiles)
        }

        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.whatsapp_media) {
          initialFiles = form.state.values.whatsapp_media.map((file) => ({
            file_type: file.media_type as FileMimeType,
            file_name: file.name || '',
            file_url: file.url || '',
            file_size: file.size || 0,
            file_id: file.file_id || '',
          }))
        }

        return (
          <MediaUpload
            onURLChange={onURLChange}
            onRemove={onRemove}
            label="Upload media"
            initialFiles={initialFiles}
            nodeVars={nodeVars}
            multiUpload
            disableFiles={
              (form.state.values.text?.length ?? 0) > 0 ? ['audio', 'application'] : ['application']
            }
          />
        )
      },
    },
    // WhatsApp template field
    {
      name: 'template',
      zodValidation: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'template'
          ? z.record(z.any()).refine((obj) => Object.keys(obj).length > 0, {
              message: 'Template is required when WhatsApp message type is template',
            })
          : z.string().optional(),
      visible: (values) =>
        values.channel === 'whatsapp' &&
        values.whatsapp_msg_type === 'template' &&
        values.from !== null,
      onChangeListenTo: ['whatsapp_msg_type', 'from', 'template', 'channel'],
      component: ({ form }) => (
        <TemplateSelect
          showApprovedOnly
          selectedSender={form.state.values.from || ''}
          form={form as unknown as GenericFormType<WhatsAppFormType>}
        />
      ),
      type: 'custom',
    },
    {
      name: 'variables',
      zodValidation: z.any(),
      className: 'overflow-hidden',
      visible: (values) =>
        values.channel === 'whatsapp' &&
        values.whatsapp_msg_type === 'template' &&
        values.template &&
        Object.keys(values.template).length > 0,
      onChangeListenTo: ['whatsapp_msg_type', 'template', 'from', 'channel'],
      component: ({ form }) => (
        <VariableEditor
          form={form as unknown as GenericFormType<WhatsAppFormType>}
          value={form.state.values.variables || { header: [], body: [] }}
          nodeVars={nodeVars}
        />
      ),
      type: 'custom',
    },
    // WhatsApp button header fields
    {
      name: 'header_type',
      label: 'Header type',
      zodValidation: z.enum(['text', 'media', 'none']).optional(),
      componentType: 'select',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_button',
      inputProps: {
        options: [
          { label: 'Text', value: 'text' },
          { label: 'Image/Video/Document', value: 'media' },
          { label: 'None', value: 'none' },
        ],
      },
    },
    {
      name: 'header_media',
      type: 'custom',
      label: 'Header media',
      zodValidation: (values) => {
        if (values.header_type === 'media') {
          return baseSchema.shape.header_media.refine(
            (media) => media && Object.keys(media).length > 0,
            {
              message: 'Header media is required when header type is media',
            },
          )
        }
        return baseSchema.shape.header_media
      },
      onChangeListenTo: ['whatsapp_msg_type', 'header_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' &&
        values.whatsapp_msg_type === 'whatsapp_button' &&
        values.header_type === 'media',
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<AgentPresetConversationNodeformData>
        onChange: (value: unknown) => void
      }) => {
        const onURLChange = (uploadedFile: RFileUploadResponse) => {
          const newMediaFile = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange(newMediaFile)
        }

        const onRemove = () => {
          onChange(null)
        }

        // Convert header_media to RFileUploadResponse format for MediaUpload
        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.header_media) {
          initialFiles = [
            {
              file_type: form.state.values.header_media.media_type as FileMimeType,
              file_name: form.state.values.header_media.name || '',
              file_url: form.state.values.header_media.url || '',
              file_size: form.state.values.header_media.size || 0,
              file_id: form.state.values.header_media.file_id || '',
            },
          ]
        }

        return (
          <MediaUpload
            onURLChange={onURLChange}
            onRemove={onRemove}
            label="Upload header media"
            initialFiles={initialFiles}
            nodeVars={nodeVars}
            multiUpload={false}
            disableFiles={['audio', 'application']}
          />
        )
      },
    },
    {
      name: 'header_text',
      label: 'Header text',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.header_type === 'text')
          return z
            .string()
            .min(1, 'Header text is required')
            .max(60, 'Header cannot exceed 60 characters')
        return z.string().optional()
      },
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'header_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' &&
        values.whatsapp_msg_type === 'whatsapp_button' &&
        values.header_type === 'text',
      inputProps: {
        placeholder: 'Enter header text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=header_text]"
          />
        ),
      },
    },
    {
      name: 'body',
      label: 'Body',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_button')
          return z
            .string()
            .min(1, 'Body is required')
            .max(1024, 'Body cannot exceed 1024 characters')
        return z.string().optional()
      },
      componentType: 'textarea',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_button',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=body]" />
        ),
      },
    },
    {
      name: 'footer',
      label: 'Footer',
      zodValidation: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_button',
      inputProps: {
        placeholder: 'Enter footer text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='footer']"
          />
        ),
      },
    },
    // WhatsApp list header field
    {
      name: 'whatsapp_list.header',
      label: 'Header (optional)',
      zodValidation: baseSchema.shape.whatsapp_list.unwrap().shape.header,
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter header text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.header']"
          />
        ),
      },
    },
    // WhatsApp list body field
    {
      name: 'whatsapp_list.body',
      label: 'Body',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list')
          return baseSchema.shape.whatsapp_list
            .unwrap()
            .shape.body.refine((body) => (body?.length || 0) > 0, {
              message: 'Body is required',
            })
        return baseSchema.shape.whatsapp_list.unwrap().shape.body
      },
      componentType: 'textarea',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.body']"
          />
        ),
      },
    },
    // WhatsApp list footer field
    {
      name: 'whatsapp_list.footer',
      label: 'Footer',
      zodValidation: baseSchema.shape.whatsapp_list.unwrap().shape.footer,
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter footer text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.footer']"
          />
        ),
      },
    },
    // WhatsApp list button field
    {
      name: 'whatsapp_list.button',
      label: 'Button',
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list')
          return baseSchema.shape.whatsapp_list
            .unwrap()
            .shape.button.refine((button) => (button?.length || 0) > 0, {
              message: 'Button is required',
            })
        return baseSchema.shape.whatsapp_list.unwrap().shape.button
      },
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter Button text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.button']"
          />
        ),
      },
    },
    // WhatsApp buttons configuration
    {
      name: 'buttons',
      type: 'array',
      label: (
        <Label className="col-span-full text-s-500 mb-2">
          Configure buttons <i className="text-xsi-400">(max 3 supported)</i>
        </Label>
      ),
      className: 'flex gap-4 p-4 rounded-md bg-bg2 w-full relative -mt-4 col-span-full',
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_button',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      defaultNewValue: (form) => ({
        title: `Button ${(form.state.values.buttons?.length || 0) + 1}`,
        id: v4(),
      }),
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_button') {
          return z
            .array(
              z.object({
                id: z.string(),
                title: z.string().min(1, 'Button text is required').max(25, 'Max 25 characters'),
              }),
            )
            .min(1, 'At least one button is required')
            .max(3, 'Maximum 3 buttons allowed')
        }
        return z.any()
      },
      addItemButton: ({ state, ...rest }) => (
        <div className="col-span-full flex justify-start bg-bg2 -mt-4 rounded-b-md items-start pb-2">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            className="ml-2 form-button"
            {...rest}
            disabled={(state?.buttons?.length || 0) >= 3}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Button
          </Button>
        </div>
      ),
      removeItemButton: <X className="w-4 h-4 absolute top-3 right-8 text-tertiary" />,
      subFields: [
        {
          name: 'buttons[0].title',
          label: 'Button Text',
          componentType: 'input',
          className: 'w-full',
          zodValidation: z.string().min(1, 'Button text is required').max(25, 'Max 25 characters'),
          inputProps: {
            placeholder: 'Enter button text',
          },
        },
      ],
    },
    // WhatsApp list configuration
    {
      name: 'sections',
      type: 'array',
      label: (
        <Label className="col-span-full text-s-500 mt-2 -mb-2">
          Configure list <i className="text-xsi-400">(max 10 supported)</i>
        </Label>
      ),
      className:
        'flex flex-col gap-2 p-4 rounded-lg bg-bg2 w-full relative mt-2 col-span-full border border-st2',
      visible: (values) =>
        values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list',
      onChangeListenTo: ['whatsapp_msg_type', 'channel'],
      defaultNewValue: (form) => ({
        title: `Section title ${(form.state.values.sections?.length || 0) + 1}`,
        rows: [
          {
            title: 'Row title 1',
            description: '',
            id: v4(),
          },
        ],
      }),
      zodValidation: (values) => {
        if (values.channel === 'whatsapp' && values.whatsapp_msg_type === 'whatsapp_list') {
          const baseSectionsSchema = z
            .array(
              z.object({
                title: z.string().max(24, 'Section title cannot exceed 24 characters'),
                rows: z
                  .array(
                    z.object({
                      id: z.string(),
                      title: z
                        .string()
                        .min(1, 'Row title is required')
                        .max(24, 'Max 24 characters'),
                      description: z.string().max(72, 'Max 72 characters').optional(),
                    }),
                  )
                  .min(1, 'At least one row is required')
                  .max(10, 'Maximum 10 rows allowed per section'),
              }),
            )
            .min(1, 'At least one section is required')
            .max(10, 'Maximum 10 sections allowed')

          return baseSectionsSchema.superRefine((sections, ctx) => {
            const totalRows = (sections ?? []).reduce(
              (
                sum: number,
                section: { rows?: { title: string; id: string; description?: string }[] },
              ) => sum + (section?.rows?.length || 0),
              0,
            )
            if (totalRows > 10) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Total number of rows across all sections cannot exceed 10.',
                path: [],
              })
            }
          })
        }
        return z.any()
      },
      addItemButton: ({ state, ...rest }) => (
        <div className="col-span-full flex justify-start items-start pb-2">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            className="ml-0 pl-0 form-button"
            {...rest}
            disabled={(state?.sections?.length || 0) >= 10}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add section
          </Button>
        </div>
      ),
      removeItemButton: <X className="w-4 h-4 absolute top-4 right-3 text-tertiary" />,
      subFields: [
        {
          name: 'sections[0].title',
          type: 'custom',
          className: 'w-full',
          zodValidation: z.string().max(24, 'Section title cannot exceed 24 characters'),
          component: ({ form, baseName }) => {
            const fieldName = `${baseName}.title` as DeepKeys<AgentPresetConversationNodeformData>
            const initialValue = form.getFieldValue(fieldName) || ''

            return (
              <InlineEdit
                name={fieldName}
                initialHtml={initialValue}
                onChange={(_, value) => {
                  form.setFieldValue(fieldName, value)
                }}
                onBlur={(_, value) => {
                  form.setFieldValue(fieldName, value)
                  form.validateField(fieldName, 'blur')
                }}
                tagName="span"
                className="border border-transparent hover:border-b-2 hover:border-b-st2 focus:border-b-2 focus:border-b-st2 p-1 pl-0 rounded flex-grow min-h-[36px] text-s-600"
                focusOnMount={false}
              />
            )
          },
        },
        {
          name: 'sections[0].rows',
          type: 'array',
          className: 'flex flex-col gap-3 w-full mt-2 relative',
          defaultNewValue: (_, field) => {
            const nextRowIndex = Array.isArray(field.state.value) ? field.state.value.length + 1 : 1
            return {
              title: `Row title ${nextRowIndex}`,
              description: '',
              id: v4(),
            }
          },
          addItemButton: ({ state, ...rest }) => {
            const totalRows = (state?.sections ?? []).reduce(
              (
                sum: number,
                section:
                  | { rows?: { title: string; id: string; description?: string }[] }
                  | undefined,
              ) => sum + (section?.rows?.length || 0),
              0,
            )
            return (
              <div className="col-span-full flex justify-start -my-4">
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  className="m-0 p-0 form-button"
                  {...rest}
                  disabled={totalRows >= 10}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add list item
                </Button>
              </div>
            )
          },
          removeItemButton: <X className="w-4 h-4 absolute text-tertiary top-0 right-0" />,
          subFields: [
            {
              name: 'sections[0].rows[0].title',
              label: 'Row title',
              componentType: 'input',
              className: 'w-full',
              zodValidation: z
                .string()
                .min(1, 'Row title is required')
                .max(24, 'Row title cannot exceed 24 characters'),
              inputProps: {
                placeholder: 'Enter row title',
              },
            },
            {
              name: 'sections[0].rows[0].description',
              label: 'Row description (optional)',
              componentType: 'input',
              className: 'w-full',
              zodValidation: z
                .string()
                .max(72, 'Row description cannot exceed 72 characters')
                .optional(),
              inputProps: {
                placeholder: 'Enter row description',
              },
            },
          ],
        },
      ],
    },
  ]
}
